import { Injectable, Signal } from "@angular/core";
import {
	CreateUser,
	UpdateEnabledUser,
	UpdateUser,
} from "../command/user.command";
import { UUID } from "../model/entityReference.model";
import { Page } from "../model/query.model";
import { User } from "../model/user.model";
import { ApiClient } from "../service/network/api.client";
import { UserStore } from "../store/user.store";
import { CompanyApi } from "./company.api";

@Injectable({
	providedIn: "root",
})
export class UserApi {
	constructor(
		private readonly api: ApiClient,
		private readonly store: UserStore,
		private readonly companyApi: CompanyApi,
	) {}

	async fetch(id: string) {
		const user = await this.api.get<User>("/user/" + id);
		this.store.saveUser(await this.sanitize(user));
	}

	public async fetchAll(): Promise<void> {
		const page = await this.api.get<Page<User>>("/user");
		this.store.saveUsers(await this.sanitizeAll(page.content));
	}

	public async create(command: CreateUser) {
		await this.api.post("/user/create", command);
		await this.fetch(command.id);
	}

	public async update(command: UpdateUser) {
		await this.api.post("/user/update", command);
		await this.fetch(command.id);
	}

	public async updateEnabled(command: UpdateEnabledUser) {
		await this.api.post("/user/update-user-enabled", command);
		await this.fetch(command.id);
	}

	public all(): Signal<User[]> {
		return this.store.entities;
	}

	/**
	 * Asks the server whether another user already has this email; the user
	 * identified by `excludeUserId` (the one being edited) is ignored.
	 *
	 * Requires the hardened backend, where GET /user/exists answers `{exists}`
	 * (see "Backend compatibility" in the README). An older backend returns a
	 * UserView or null instead; in that case the check degrades to "not taken"
	 * rather than throwing.
	 */
	public async exists(email: string, excludeUserId?: string): Promise<boolean> {
		const params: Record<string, string> = { email };
		if (excludeUserId) params["excludeUserId"] = excludeUserId;

		const response = await this.api.get<{ exists?: boolean } | null>(
			"/user/exists",
			params,
		);

		return response?.exists === true;
	}

	private async sanitizeAll(users: User[]): Promise<User[]> {
		const result = [];
		for (const user of users) {
			const sanitizedUsed = await this.sanitize(user);
			result.push(sanitizedUsed);
		}
		return result;
	}

	private async sanitize(user: User): Promise<User> {
		if (!user) return user;

		let company = this.companyApi.syncById(user.companyId);
		if (!company) {
			await this.companyApi.fetch(user.companyId);
		}
		company = this.companyApi.syncById(user.companyId);
		user.company = company?.name ?? "";

		return user;
	}

	public me(): Signal<User | undefined> {
		return this.store.me;
	}

	public byId(id: UUID): Signal<User | undefined> {
		this.store.updateById(id);
		return this.store.byIdEntity;
	}

	public syncById(id: string): User | undefined {
		return this.store.syncById(id);
	}
}
